// ─── React export ─────────────────────────────────────────────────────────────
// The generated site, converted into a Vite + React project a developer can
// open and keep working in.
//
// This is a translation, and it is worth being honest about what that means: it
// produces one component per page holding that page's markup, not the component
// tree someone would write by hand. What it does give is a real project — JSX,
// a router, a stylesheet, `npm install && npm run dev` — instead of a folder of
// .html files, which is what people buying the code actually asked for.
//
// Everything here is pure and runs in the browser, so DOMParser does the
// parsing. That matters: a regex converter gets `<div class="a" data-x='b'>`
// wrong in a dozen ways, while the browser's own parser handles exactly what
// the browser handled when the site was generated.

import { toPages, DEFAULT_PAGE, stripTrailingArtifacts, withSvgNamespace } from '@/lib/sitePages';

// ── Attribute translation ─────────────────────────────────────────────────────

// HTML attribute → JSX prop. Anything not listed passes through unchanged,
// which is what `data-*` and `aria-*` need.
const PROP_NAMES = {
  class: 'className', for: 'htmlFor', tabindex: 'tabIndex', readonly: 'readOnly',
  maxlength: 'maxLength', minlength: 'minLength', colspan: 'colSpan', rowspan: 'rowSpan',
  srcset: 'srcSet', autocomplete: 'autoComplete', autofocus: 'autoFocus',
  autoplay: 'autoPlay', playsinline: 'playsInline', novalidate: 'noValidate',
  contenteditable: 'contentEditable', spellcheck: 'spellCheck', crossorigin: 'crossOrigin',
  datetime: 'dateTime', enctype: 'encType', formaction: 'formAction',
  frameborder: 'frameBorder', usemap: 'useMap', accesskey: 'accessKey',
  inputmode: 'inputMode', cellpadding: 'cellPadding', cellspacing: 'cellSpacing',
  'accept-charset': 'acceptCharset', 'http-equiv': 'httpEquiv',
};

// Present-means-true in HTML; in JSX they are written bare.
const BOOLEAN_ATTRS = new Set([
  'autoplay', 'autofocus', 'checked', 'controls', 'default', 'defer', 'disabled',
  'hidden', 'loop', 'multiple', 'muted', 'novalidate', 'open', 'playsinline',
  'readonly', 'required', 'reversed', 'selected', 'async', 'ismap', 'nomodule',
]);

const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** `font-size` → `fontSize`, leaving custom properties (`--brand`) alone. */
function cssPropToJs(name) {
  if (name.startsWith('--')) return `'${name}'`;
  return name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

/** `color:red;font-size:12px` → `{{ color: 'red', fontSize: '12px' }}` */
function styleToObject(value) {
  const entries = String(value || '')
    .split(';')
    .map((d) => d.trim())
    .filter(Boolean)
    .map((d) => {
      const i = d.indexOf(':');
      if (i === -1) return null;
      const prop = d.slice(0, i).trim();
      const val = d.slice(i + 1).trim().replace(/'/g, "\\'");
      return prop ? `${cssPropToJs(prop)}: '${val}'` : null;
    })
    .filter(Boolean);

  return entries.length ? `{{ ${entries.join(', ')} }}` : null;
}

// ── Markup → JSX ──────────────────────────────────────────────────────────────

/**
 * Text as it can appear inside JSX.
 *
 * Braces are the only real hazard: `{` opens an expression, so a price written
 * as "{price}" in the copy would become an undefined variable and crash the
 * page at render rather than at build. `<` and `>` cannot occur in a parsed
 * text node at all, so nothing else needs escaping.
 */
function jsxText(text) {
  return String(text).replace(/[{}]/g, (c) => `{'${c}'}`);
}

const INDENT = (depth) => '  '.repeat(depth);

/**
 * Convert one page's markup to a JSX fragment.
 *
 * @param {string} html
 * @param {{routes: Map<string,string>}} ctx  page path → route, so internal
 *        links become <Link to="…"> instead of a full page load
 * @returns {{jsx: string, usesLink: boolean}}
 */
export function htmlToJsx(html, { routes = new Map() } = {}) {
  const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
  let usesLink = false;

  const attrsFor = (el) => {
    const out = [];
    for (const { name, value } of Array.from(el.attributes)) {
      const lower = name.toLowerCase();

      if (lower === 'style') {
        const obj = styleToObject(value);
        if (obj) out.push(`style=${obj}`);
        continue;
      }
      if (BOOLEAN_ATTRS.has(lower) && (value === '' || value === lower || value === 'true')) {
        out.push(PROP_NAMES[lower] || lower);
        continue;
      }
      const prop = PROP_NAMES[lower] || name;
      out.push(`${prop}="${String(value).replace(/"/g, '&quot;')}"`);
    }
    return out;
  };

  const render = (node, depth) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue;
      if (!text.trim()) return '';
      return `${INDENT(depth)}${jsxText(text.trim())}`;
    }
    if (node.nodeType === Node.COMMENT_NODE) {
      return `${INDENT(depth)}{/* ${String(node.nodeValue).trim().replace(/\*\//g, '*\\/')} */}`;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const tag = node.nodeName.toLowerCase();
    // The shared script is imported and run from App, not inlined per page.
    if (tag === 'script') return '';

    let name = tag;
    let attrs = attrsFor(node);

    // An internal link becomes a router link, which is the whole reason a
    // developer wants this as React rather than as pages.
    if (tag === 'a') {
      const href = node.getAttribute('href') || '';
      const route = routes.get(href.replace(/^\.?\//, '').split(/[?#]/)[0]);
      if (route) {
        usesLink = true;
        name = 'Link';
        attrs = attrs.filter((a) => !a.startsWith('href='));
        attrs.unshift(`to="${route}"`);
      }
    }

    const open = attrs.length ? `<${name} ${attrs.join(' ')}` : `<${name}`;

    if (VOID_ELEMENTS.has(tag)) return `${INDENT(depth)}${open} />`;

    const children = Array.from(node.childNodes)
      .map((c) => render(c, depth + 1))
      .filter(Boolean);

    if (!children.length) return `${INDENT(depth)}${open}></${name}>`;

    return `${INDENT(depth)}${open}>\n${children.join('\n')}\n${INDENT(depth)}</${name}>`;
  };

  const jsx = Array.from(doc.body.childNodes)
    .map((n) => render(n, 3))
    .filter(Boolean)
    .join('\n');

  return { jsx, usesLink };
}

// ── Project scaffolding ───────────────────────────────────────────────────────

/** `about.html` → `{ component: 'About', route: '/about', file: 'About.jsx' }` */
export function pageMeta(path) {
  if (path === DEFAULT_PAGE) return { component: 'Home', route: '/', file: 'Home.jsx' };

  const base = String(path).replace(/\.html?$/i, '');
  const component = base
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join('') || 'Page';

  return { component, route: `/${base.replace(/[^A-Za-z0-9/-]+/g, '-')}`, file: `${component}.jsx` };
}

const pkg = (name) => JSON.stringify({
  name: name || 'website',
  private: true,
  version: '0.1.0',
  type: 'module',
  scripts: { dev: 'vite', build: 'vite build', preview: 'vite preview' },
  dependencies: {
    react: '^19.0.0',
    'react-dom': '^19.0.0',
    'react-router-dom': '^7.1.0',
  },
  devDependencies: {
    '@vitejs/plugin-react': '^5.0.0',
    vite: '^7.0.0',
  },
}, null, 2) + '\n';

/**
 * The site's original script, kept whole and called once the page is on screen.
 *
 * The shim is not optional. Generated scripts wire themselves up inside a
 * DOMContentLoaded listener — the generator's own rules require it — and that
 * event fired long before React mounted anything. Without this the exported
 * site renders perfectly and nothing works: no menus, no filters, no cart. So
 * the listener is captured during execution and invoked immediately after.
 */
const legacyJs = (js) => `// The generated site's JavaScript, unchanged.
//
// It was written for a static page: it waits for DOMContentLoaded and then
// queries the document. By the time React has mounted, that event is long
// past, so the listener is captured here and called once the markup exists.
//
// Porting this into hooks and state is the natural next step — it is kept
// intact so the exported site behaves exactly like the one you generated.
export default function runSiteScript() {
  const realAdd = document.addEventListener.bind(document);
  const pending = [];
  document.addEventListener = (type, fn, opts) => {
    if (type === 'DOMContentLoaded') { pending.push(fn); return; }
    realAdd(type, fn, opts);
  };

  try {
${String(js || '').split('\n').map((l) => (l ? `    ${l}` : '')).join('\n')}
  } catch (err) {
    console.error('[site script]', err);
  } finally {
    document.addEventListener = realAdd;
  }

  pending.forEach((fn) => {
    try { fn(new Event('DOMContentLoaded')); } catch (err) { console.error('[site script]', err); }
  });
}
`;

/**
 * Build a Vite + React project from a generated site.
 *
 * Same shape as buildSiteFiles: `[{ name, content }]`, ready for JSZip.
 *
 * @param {object} site  { title, pages, css, js, icon }
 * @returns {Array<{name: string, content: string}>}
 */
export function buildReactFiles(site) {
  const pages = toPages(site);
  const title = site?.title || 'Website';
  const icon = site?.icon ?? site?.icon_svg ?? '';
  const css = site?.css ?? site?.css_content ?? '';
  // Rows saved before the parser learned to drop them can still carry a stray
  // tag after the last line of script — one such site shipped with
  // `</arg_value>` on the end, which is a syntax error. In a downloaded project
  // that is a build failure on `npm run dev`, so it is stripped here too.
  const js = stripTrailingArtifacts(site?.js ?? site?.js_content ?? '');

  const metas = pages.map((p) => ({ ...pageMeta(p.path), path: p.path, html: p.html }));
  const routes = new Map(metas.map((m) => [m.path, m.route]));

  const files = [];

  // ── pages ──────────────────────────────────────────────────────────────────
  for (const m of metas) {
    const { jsx, usesLink } = htmlToJsx(m.html, { routes });
    files.push({
      name: `src/pages/${m.file}`,
      content: `${usesLink ? "import { Link } from 'react-router-dom';\n\n" : ''}export default function ${m.component}() {
  return (
    <>
${jsx}
    </>
  );
}
`,
    });
  }

  // ── app shell ──────────────────────────────────────────────────────────────
  const imports = metas.map((m) => `import ${m.component} from './pages/${m.file}';`).join('\n');
  const routeEls = metas.map((m) => `        <Route path="${m.route}" element={<${m.component} />} />`).join('\n');

  files.push({ name: 'src/App.jsx', content: `import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
${imports}
import runSiteScript from './legacy.js';
import './index.css';

// The site's own script is re-run on every navigation, because it binds its
// handlers to elements that React has just replaced.
function SiteScript() {
  const { pathname } = useLocation();
  useEffect(() => { runSiteScript(); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <SiteScript />
      <Routes>
${routeEls}
      </Routes>
    </BrowserRouter>
  );
}
` });

  files.push({ name: 'src/main.jsx', content: `import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
` });

  files.push({ name: 'src/index.css', content: css });
  files.push({ name: 'src/legacy.js', content: legacyJs(js) });

  // ── project files ──────────────────────────────────────────────────────────
  // Tailwind stays on the CDN because that is what the site was generated
  // against; swapping it for a real install is a deliberate change, not
  // something an export should make on someone's behalf.
  files.push({ name: 'index.html', content: `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${String(title).replace(/</g, '&lt;')}</title>
${icon ? '    <link rel="icon" href="/favicon.svg" type="image/svg+xml" />\n' : ''}    <script src="https://cdn.tailwindcss.com"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.jsx"></script>
  </body>
</html>
` });

  files.push({ name: 'vite.config.js', content: `import { defineConfig } from 'vite';
import react from '@vitejs/plugin-react';

export default defineConfig({
  plugins: [react()],
});
` });

  files.push({ name: 'package.json', content: pkg(String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) });
  files.push({ name: '.gitignore', content: 'node_modules\ndist\n.DS_Store\n' });

  if (icon) files.push({ name: 'public/favicon.svg', content: withSvgNamespace(icon) });

  files.push({ name: 'README.md', content: `# ${title}

A React version of your BuildersLab site, built with Vite.

## Running it

\`\`\`bash
npm install
npm run dev
\`\`\`

\`npm run build\` writes a production bundle to \`dist/\`.

## What is here

- \`src/pages/\` — one component per page of your site
- \`src/App.jsx\` — routing, and the hook that runs the site script
- \`src/index.css\` — your stylesheet, unchanged
- \`src/legacy.js\` — your site's JavaScript, unchanged

## Worth knowing

This is a faithful conversion, not a rewrite. Each page's markup became one
component, so you get a real React project to work in — but not the component
tree you would design by hand. Pulling repeated markup out into components
(a card, a nav, a section) is the natural first change to make.

\`src/legacy.js\` holds your original script. It was written for a static page
and waits for \`DOMContentLoaded\`, which has already fired by the time React
mounts — so it is called explicitly after each render instead. Moving that
logic into hooks and state is the second change to make.

Tailwind is loaded from the CDN in \`index.html\`, matching how the site was
generated. For production, install Tailwind properly and delete that script tag.
` });

  return files;
}
